import {useAssistantAutoOpen} from '@/assistant-panel-state'
import {editorBlocksToHMBlockNodes} from '@seed-hypermedia/client'
import type {HMDocument, HMResourceVisibility, UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {
  createInspectNavRouteFromRoute,
  hmId,
  routeToUrl,
  useJoinSite,
  useUniversalAppContext,
  useUniversalClient,
} from '@shm/shared'
import {documentCreationMachine, inferDocumentSchema, resolveDocumentCreationDestination} from '@shm/shared'
import {buildCollectionDraftSeed} from '@shm/shared/collection'
import {DEFAULT_GATEWAY_URL} from '@shm/shared/constants'
import {useIsSiteOwner, useSelectedAccountCapability} from '@shm/shared/models/capabilities'
import {createDefaultCollectionQueryBlock, deriveDocumentType} from '@shm/shared/models/document-machine'
import {useAccount, useResource} from '@shm/shared/models/entity'
import {queryQueryBlock} from '@shm/shared/models/queries'
import {hmIdPathToEntityQueryPath} from '@shm/shared/utils/path-api'
import {isNotificationEventRead} from '@shm/shared/models/notification-read-logic'
import {hmIdToURL, unpackHmId} from '@shm/shared/utils/entity-id-url'
import {getActivityBellTarget, getRouteSpaceUid, isActivityOpen} from '@shm/shared/routes'
import {useNavigate, useNavRoute} from '@shm/shared/utils/navigation'
import {isPendingSpaceUid} from '@shm/shared/utils/pending-space'
import {useSiteAgents} from '@shm/ui/assistant-panel-toggle'
import {useCollapseActivity} from '@shm/ui/activity-panel'
import {Button} from '@shm/ui/button'
import {accountMenuContentClassName, AccountMenuIdentity, AccountMenuSection} from '@shm/ui/account-menu'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shm/ui/components/dropdown-menu'
import {createCopyLinkMenuItem} from '@shm/ui/copy-link-menu'
import {copyUrlToClipboardWithFeedback} from '@shm/ui/copy-to-clipboard'
import {useDocumentMaintenance} from '@shm/ui/document-maintenance'
import {createDocumentVersionsPanelRoute} from '@shm/ui/document-versions-panel'
import {DocumentCreateButton} from '@shm/ui/document-create-button'
import {HypermediaHostBanner} from '@shm/ui/hm-host-banner'
import {HMIcon} from '@shm/ui/hm-icon'
import {Add} from '@shm/ui/icons'
import {JoinButton} from '@shm/ui/join-button'
import {clearInviteJoin, hasInviteJoin, shouldAutoJoin} from './invite-join'
import {MobilePanelSheet} from '@shm/ui/mobile-panel-sheet'
import {MenuItemType} from '@shm/ui/options-dropdown'
import {createEmailSubscribersMenuItem} from '@shm/ui/site-email-subscribers'
import {toast} from '@shm/ui/toast'
import {Tooltip} from '@shm/ui/tooltip'
import {useAppDialog} from '@shm/ui/universal-dialog'
import {useMedia} from '@shm/ui/use-media'
import {cn} from '@shm/ui/utils'
import {
  Bell,
  Bot,
  ContactRound,
  ExternalLink,
  FilePlus2,
  Globe,
  Grid3X3,
  History,
  Import as ImportIcon,
  Layers,
  LayoutList,
  KeyRound,
  ListChecks,
  LogOut,
  Plus,
  Search,
  User,
  SquarePen,
} from 'lucide-react'
import {nanoid} from 'nanoid'
import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useActorRef, useSelector} from '@xstate/react'
import {useQuery} from '@tanstack/react-query'
import {LogoutDialog, useCreateAccount, useLocalKeyPair} from './auth'
import {createWebDocumentDraft, createWebDocumentDraftFromMarkdownFile} from './document-edit/web-create-draft'
import {listWebDocChildDrafts} from './document-edit/web-draft-db'
import {useWebCanEdit} from './document-edit/use-web-can-edit'
import {WebSpaceSettingsMenuItem} from './components/web-space-settings-menu'
import {getVaultAccountSettingsUrl} from './vault-links'
import {useCreateSpaceDialog, useHasExistingSpace} from './web-create-space-dialog'
import {useWebNotificationInbox, useWebNotificationReadState} from './web-notifications'

export function useWebMenuItems(docId: UnpackedHypermediaId, options?: {includeInspect?: boolean}): MenuItemType[] {
  const route = useNavRoute()
  const navigate = useNavigate()
  const {onCopyReference, onPushReference, origin, originHomeId, experiments} = useUniversalAppContext()
  const includeInspect = options?.includeInspect !== false
  // Email subscribers is offered on the home document for the site owner,
  // matching the desktop document options menu.
  const {isSiteOwner} = useIsSiteOwner(docId.path?.length ? undefined : docId.uid)
  const inspectRoute = useMemo(() => {
    if (!includeInspect) return null
    const wrappedRoute = createInspectNavRouteFromRoute(route)
    return wrappedRoute?.key === 'inspect' ? wrappedRoute : null
  }, [includeInspect, route])
  const allDocumentsId = originHomeId ? hmId(originHomeId.uid) : hmId(docId.uid)

  return useMemo(
    () => [
      createCopyLinkMenuItem({
        advanced: experiments?.advancedCopyLinkOptions,
        canonical: {
          copy:
            (onCopyReference ? () => onCopyReference(docId) : null) ??
            (typeof window !== 'undefined' ? () => copyUrlToClipboardWithFeedback(window.location.href, 'Link') : null),
        },
        gateway: {
          copy: async () => {
            const url = routeToUrl(route, {hostname: origin ?? DEFAULT_GATEWAY_URL, originHomeId})
            if (url) await copyUrlToClipboardWithFeedback(url, 'Gateway')
            onPushReference?.(docId)
          },
        },
        hypermedia: {
          copy: () => copyUrlToClipboardWithFeedback(hmIdToURL(docId), 'Hypermedia'),
        },
      }),
      {
        key: 'versions',
        label: 'Versions history',
        icon: <History className="size-4" />,
        onClick: () => {
          navigate({
            key: 'document',
            id: docId,
            panel: createDocumentVersionsPanelRoute(docId),
          })
        },
      },
      {
        key: 'directory',
        label: 'Sub documents',
        icon: <Layers className="size-4" />,
        onClick: () => navigate({key: 'directory', id: docId}),
      },
      {
        key: 'all-documents',
        label: 'All Documents',
        icon: <LayoutList className="size-4" />,
        onClick: () => navigate({key: 'all-documents', id: allDocumentsId}),
      },
      ...(isSiteOwner ? [createEmailSubscribersMenuItem({navigate})] : []),
      ...(inspectRoute
        ? [
            {
              key: 'inspect',
              label: 'Inspect Document',
              icon: <Search className="size-4" />,
              onClick: () => {
                navigate(inspectRoute)
              },
            } satisfies MenuItemType,
          ]
        : []),
    ],
    [
      allDocumentsId,
      docId,
      inspectRoute,
      isSiteOwner,
      navigate,
      onCopyReference,
      onPushReference,
      origin,
      originHomeId,
      route,
      experiments?.advancedCopyLinkOptions,
    ],
  )
}

/** Builds the web document creation submenu item for the document options menu. */
export function useWebCreateDocumentMenuItem({
  locationId,
  signingAccountId,
  canCreate,
  canCreateChildren = true,
  capabilityCid,
}: {
  locationId: UnpackedHypermediaId
  signingAccountId?: string
  canCreate: boolean
  canCreateChildren?: boolean
  capabilityCid?: string
}): {
  menuItem: MenuItemType | null
  content: ReactNode
} {
  const navigate = useNavigate()
  const importInputRef = useRef<HTMLInputElement>(null)
  const client = useUniversalClient()

  const createDraft = useCallback(
    (visibility?: HMResourceVisibility, collection = false) => {
      if (!signingAccountId) return
      console.log('[web-create-doc] menu createDraft', {
        locationId: locationId.id,
        visibility,
        signingAccountId,
      })
      const seed = collection ? buildCollectionDraftSeed(crypto.randomUUID()) : null
      void createWebDocumentDraft({
        client,
        locationId,
        signingAccountId,
        visibility,
        metadata: seed?.metadata,
        content: seed ? editorBlocksToHMBlockNodes(seed.content) : undefined,
        capabilityCid,
        persist: false,
        ...(collection
          ? {content: editorBlocksToHMBlockNodes([createDefaultCollectionQueryBlock(nanoid(8))]), persist: true}
          : {}),
        navigate: (route) => navigate(route),
      }).catch((error) => toast.error(error instanceof Error ? error.message : 'Could not create document'))
    },
    [client, capabilityCid, locationId, navigate, signingAccountId],
  )

  const menuItem = useMemo<MenuItemType | null>(() => {
    if (!canCreate || !canCreateChildren || !signingAccountId) return null
    return {
      key: 'new',
      label: 'New',
      icon: <Add className="size-4" />,
      children: [
        {
          key: 'new-document',
          label: 'Document',
          icon: <FilePlus2 className="size-4" />,
          onClick: () => createDraft('PUBLIC'),
        },
        {
          key: 'new-collection',
          label: 'Collection',
          icon: <Grid3X3 className="size-4" />,
          onClick: () => createDraft('PUBLIC', true),
        },
        {
          key: 'import',
          label: 'Import Markdown File',
          icon: <ImportIcon className="size-4" />,
          onClick: () => importInputRef.current?.click(),
        },
      ],
    }
  }, [canCreate, canCreateChildren, createDraft, signingAccountId])

  return {
    menuItem,
    content:
      canCreate && canCreateChildren && signingAccountId ? (
        <input
          ref={importInputRef}
          type="file"
          accept=".md,.markdown,text/markdown,text/plain"
          className="hidden"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0]
            event.currentTarget.value = ''
            if (!file) return
            toast.promise(
              createWebDocumentDraftFromMarkdownFile({
                client,
                file,
                locationId,
                signingAccountId,
                capabilityCid,
                navigate: (route) => navigate(route),
              }),
              {
                loading: 'Importing Markdown…',
                success: 'Markdown imported.',
                error: 'Failed to import Markdown.',
              },
            )
          }}
        />
      ) : null,
  }
}

function WebActorCreateButton({
  locationId,
  signingAccountId,
  currentIsCollection,
  parentId,
  parentIsCollection,
  canEditParent,
  capabilityCid,
  schema,
  onFinished,
}: {
  locationId: UnpackedHypermediaId
  signingAccountId: string
  currentIsCollection: boolean
  parentId?: UnpackedHypermediaId
  parentIsCollection: boolean
  canEditParent: boolean
  capabilityCid?: string
  schema?: ReturnType<typeof inferDocumentSchema>
  onFinished: () => void
}) {
  const navigate = useNavigate()
  const client = useUniversalClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const destination = resolveDocumentCreationDestination(locationId, {
    canEditCurrent: true,
    currentIsCollection,
    parentId,
    parentIsCollection,
    canEditParent,
  })
  const actor = useActorRef(documentCreationMachine, {
    input: {
      currentId: locationId,
      resolve: async () => ({
        canEditCurrent: true,
        currentIsCollection,
        parentId,
        parentIsCollection,
        canEditParent,
        capabilityCid,
        schema,
      }),
      create: async (request) => {
        const seed = request.kind === 'collection' ? buildCollectionDraftSeed(crypto.randomUUID()) : null
        const result = await createWebDocumentDraft({
          client,
          locationId: request.destination,
          signingAccountId,
          visibility: 'PUBLIC',
          metadata: {...seed?.metadata, ...request.metadata},
          content: seed ? editorBlocksToHMBlockNodes([createDefaultCollectionQueryBlock(nanoid(8))]) : undefined,
          capabilityCid,
          persist: request.kind === 'collection',
          navigate: (route) => navigate(route),
        })
        return result.routeId
      },
    },
  })
  const snapshot = useSelector(actor, (state) => state)

  useEffect(() => {
    if (snapshot.value !== 'failedCreation') return
    toast.error(snapshot.context.error instanceof Error ? snapshot.context.error.message : 'Could not create document')
    actor.send({type: 'retry.requested'})
  }, [actor, snapshot.context.error, snapshot.value])

  useEffect(() => {
    const input = inputRef.current
    if (!input) return
    input.addEventListener('cancel', onFinished)
    return () => input.removeEventListener('cancel', onFinished)
  }, [onFinished])

  return (
    <>
      <DocumentCreateButton
        disabled={JSON.stringify(snapshot.value) !== JSON.stringify({resolved: 'ready'})}
        showSubdocument={snapshot.can({type: 'create.requested', kind: 'subdocument'})}
        importLabel="Import Markdown File"
        onCreate={(kind) => actor.send({type: 'create.requested', kind})}
        onImport={() => {
          actor.send({type: 'import.requested'})
          if (actor.getSnapshot().output?.type === 'import') inputRef.current?.click()
        }}
      />
      <input
        ref={inputRef}
        type="file"
        accept=".md,.markdown,text/markdown,text/plain"
        className="hidden"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0]
          event.currentTarget.value = ''
          if (!file) return
          toast.promise(
            createWebDocumentDraftFromMarkdownFile({
              client,
              file,
              locationId: destination,
              signingAccountId,
              capabilityCid,
              schema,
              navigate: (route) => navigate(route),
            }),
            {loading: 'Importing Markdown…', success: 'Markdown imported.', error: 'Failed to import Markdown.'},
          )
          onFinished()
        }}
      />
    </>
  )
}

/** Deferred actor-backed document creation control for the web document toolbar. */
export function WebDocumentCreateButton({
  locationId,
  currentDocument,
  canEdit,
  signingAccountId,
  capabilityCid,
}: {
  locationId: UnpackedHypermediaId
  currentDocument?: HMDocument
  canEdit: boolean
  signingAccountId?: string
  capabilityCid?: string
}) {
  const client = useUniversalClient()
  const parentId = locationId.path?.length ? hmId(locationId.uid, {path: locationId.path.slice(0, -1)}) : undefined
  const parent = useResource(currentDocument ? parentId : undefined)
  const parentDocument = parent.data?.type === 'document' ? parent.data.document : undefined
  const parentAccess = useWebCanEdit(currentDocument ? parentId : undefined)
  const currentIsCollection =
    !!currentDocument && deriveDocumentType(currentDocument.content, locationId) === 'collection'
  const parentIsCollection =
    !!parentDocument && !!parentId && deriveDocumentType(parentDocument.content, parentId) === 'collection'
  const schemaId = currentIsCollection ? locationId : parentIsCollection && parentAccess.canEdit ? parentId : undefined
  const children = useQuery(
    queryQueryBlock(
      client,
      schemaId
        ? {query: {includes: [{space: schemaId.uid, path: hmIdPathToEntityQueryPath(schemaId.path), mode: 'Children'}]}}
        : null,
    ),
  )
  const draftChildren = useQuery({
    queryKey: ['web-document-creation-drafts', schemaId?.id],
    queryFn: () => listWebDocChildDrafts(schemaId!.uid, schemaId!.path ?? []),
    enabled: !!schemaId,
  })
  const schema = useMemo(
    () =>
      schemaId
        ? inferDocumentSchema({
            collectionId: schemaId,
            publishedChildren: children.data?.results ?? [],
            draftChildren: (draftChildren.data ?? []).flatMap((draft) => {
              const id = unpackHmId(draft.docId)
              return id ? [{id, metadata: draft.metadata}] : []
            }),
          })
        : undefined,
    [children.data?.results, draftChildren.data, schemaId],
  )
  const [generation, setGeneration] = useState(0)
  const loading =
    !currentDocument ||
    (!!parentId && parent.isLoading) ||
    (!!parentId && parentAccess.capabilitiesLoading) ||
    (!!schemaId && (children.isLoading || draftChildren.isLoading))

  if (loading) return <DocumentCreateButton disabled onCreate={() => {}} onImport={() => {}} />
  if (!canEdit || !signingAccountId) return null

  return (
    <WebActorCreateButton
      key={generation}
      locationId={locationId}
      signingAccountId={signingAccountId}
      currentIsCollection={currentIsCollection}
      parentId={parentId}
      parentIsCollection={parentIsCollection}
      canEditParent={parentAccess.canEdit}
      capabilityCid={
        parentIsCollection && parentAccess.canEdit
          ? parentAccess.capability?.id === '_owner'
            ? undefined
            : parentAccess.capability?.id
          : capabilityCid
      }
      schema={schema}
      onFinished={() => setGeneration((value) => value + 1)}
    />
  )
}

function PlaceholderAvatar({onClick}: {onClick: () => void}) {
  return (
    <button
      onClick={onClick}
      className="flex size-8 cursor-pointer items-center justify-center rounded-full border border-dashed border-gray-400"
    >
      <User className="size-4 text-gray-400" />
    </button>
  )
}

/**
 * Site-header join button or avatar with notifications bell
 */
export function WebHeaderActions({siteUid}: {siteUid: string}) {
  const maintenance = useDocumentMaintenance()
  const accountMenuTriggerRef = useRef<HTMLButtonElement>(null)
  const openMaintenanceOnMenuClose = useRef(false)
  const keyPair = useLocalKeyPair()
  const accountId = keyPair?.delegatedAccountUid ?? keyPair?.id
  const {content: createAccountContent, createAccount} = useCreateAccount({})
  const {isJoined, isLoaded: contactsLoaded, joinSite} = useJoinSite({siteUid})
  const capability = useSelectedAccountCapability(hmId(siteUid))
  const accountRole = capability?.role
    ? {owner: 'Owner', writer: 'Writer', agent: 'Agent', none: undefined}[capability.role]
    : isJoined
      ? 'Member'
      : undefined
  const logoutDialog = useAppDialog(LogoutDialog, {showCloseButton: false})
  const {open: openCreateSpaceDialog, content: createSpaceDialogContent} = useCreateSpaceDialog()
  const universalClient = useUniversalClient()
  const {data: existingSpace} = useHasExistingSpace(accountId, universalClient)
  const canCreateSpace = !existingSpace?.exists

  const myAccount = useAccount(accountId || undefined, {
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
  })

  const account = useMemo(() => {
    if (!myAccount.data?.id) return null
    return {
      id: hmId(myAccount.data.id.uid, {latest: true}),
      metadata: myAccount.data.metadata ?? undefined,
    }
  }, [myAccount.data])

  const navigate = useNavigate()
  const vaultAccountSettingsUrl = getVaultAccountSettingsUrl({
    vaultUrl: keyPair?.vaultUrl,
    accountUid: keyPair?.delegatedAccountUid,
  })
  const media = useMedia()
  const isMobile = media.xs
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  // The panel's entry points live in the site header (see @shm/ui/assistant-panel-toggle); the
  // space's own agents only matter here for the first-arrival auto-open.
  const siteAgents = useSiteAgents(siteUid)
  const hasSiteAgents = !!siteAgents.serverUrl
  // First arrival: a signed-in reader of a space that publishes agents finds the panel already
  // open, once, on a viewport wide enough to show it beside the page. Closing it is remembered.
  useAssistantAutoOpen(!!keyPair && hasSiteAgents && siteAgents.publishesAgents && !isMobile)
  // Hide join button if inside a pending space draft.
  const isPendingSpace = isPendingSpaceUid(siteUid)

  // Someone who just accepted an invitation is joined on arrival: the sign-in
  // page left a marker, and this is where the Join button would otherwise show.
  const autoJoinStarted = useRef(false)
  useEffect(() => {
    if (autoJoinStarted.current) return
    if (
      !shouldAutoJoin({
        pending: hasInviteJoin(),
        signedIn: !!keyPair && !!accountId,
        contactsLoaded,
        isJoined,
        isPendingSpace,
      })
    ) {
      if (isJoined && hasInviteJoin()) clearInviteJoin()
      return
    }
    autoJoinStarted.current = true
    joinSite()
      .then(() => clearInviteJoin())
      .catch((error) => {
        autoJoinStarted.current = false
        console.error('[invite-join] could not join the site automatically', error)
      })
  }, [keyPair, accountId, contactsLoaded, isJoined, isPendingSpace, joinSite])

  // Show the join button if not joined the site
  if (!keyPair) {
    return (
      <>
        <div className="flex items-center gap-2">
          <NotifsButton signedIn={false} />
          <PlaceholderAvatar onClick={() => createAccount({source: 'login'})} />
          {isPendingSpace ? null : <JoinButton onClick={() => createAccount({source: 'join'})} />}
        </div>
        {createAccountContent}
      </>
    )
  }

  const joinButton = !isJoined && !isPendingSpace ? <JoinButton onClick={() => joinSite()} /> : null

  // Show the avatar and bell when logged in.
  const avatarIcon = (
    <HMIcon
      id={account?.id ?? hmId(accountId!, {latest: true})}
      name={account?.metadata?.name}
      icon={account?.metadata?.icon}
      size={32}
    />
  )

  // When the account already has a site, the dropdown shows a link to it.
  const mySiteUrl = account?.metadata?.siteUrl || null
  const mySiteLabel = mySiteUrl
    ? mySiteUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '')
    : account?.metadata?.name || 'My space'
  const goToMySite = () => {
    if (mySiteUrl) window.open(mySiteUrl, '_blank', 'noopener,noreferrer')
    else if (accountId) navigate({key: 'document', id: hmId(accountId, {latest: true})})
  }

  const accountHeader = (
    <div className="flex items-center gap-3 px-4 py-4">
      <AccountMenuIdentity
        avatar={
          <HMIcon
            id={account?.id ?? hmId(accountId!, {latest: true})}
            name={account?.metadata?.name}
            icon={account?.metadata?.icon}
            size={40}
          />
        }
        name={account?.metadata?.name}
        role={accountRole}
        email={keyPair.emailPrevalidation?.email}
      />
    </div>
  )

  const menuItems = (
    <>
      <AccountMenuSection>Settings</AccountMenuSection>
      <button
        className="hover:bg-accent flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left"
        onClick={() => {
          if (accountId) {
            navigate({key: 'profile', id: hmId(accountId, {latest: true})})
          }
          setMobileMenuOpen(false)
        }}
      >
        <ContactRound className="size-4" />
        <span className="text-sm">My profile</span>
      </button>
      <button
        className="hover:bg-accent flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left"
        onClick={() => {
          navigate({key: 'agents'})
          setMobileMenuOpen(false)
        }}
      >
        <Bot className="size-4" />
        <span className="text-sm">Agents</span>
      </button>
      <button
        className="hover:bg-accent flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left disabled:opacity-50"
        onClick={() => {
          if (vaultAccountSettingsUrl) {
            window.open(vaultAccountSettingsUrl, '_blank')
          }
          setMobileMenuOpen(false)
        }}
        disabled={!vaultAccountSettingsUrl}
      >
        <KeyRound className="size-4" />
        <span className="text-sm">Manage accounts</span>
      </button>
      <WebSpaceSettingsMenuItem
        siteUid={siteUid}
        mobile
        onSelect={() => {
          setMobileMenuOpen(false)
          navigate({key: 'site-settings', id: hmId(siteUid)})
        }}
      />
      <button
        className="hover:bg-accent flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left"
        onClick={() => {
          navigate({key: 'drafts'})
          setMobileMenuOpen(false)
        }}
      >
        <SquarePen className="size-4" />
        <span className="text-sm">Drafts</span>
      </button>
      {maintenance ? (
        <button
          className="hover:bg-accent flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left"
          aria-haspopup="dialog"
          onClick={() => {
            setMobileMenuOpen(false)
            if (accountMenuTriggerRef.current) maintenance.openDialog(accountMenuTriggerRef.current)
          }}
        >
          <ListChecks className="size-4" />
          <span className="text-sm">Document maintenance</span>
        </button>
      ) : null}
      <div className="bg-border h-px" />
      <AccountMenuSection>My sites</AccountMenuSection>
      {canCreateSpace ? (
        <button
          className="hover:bg-accent flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left font-normal text-green-600 dark:text-green-500"
          onClick={() => {
            setMobileMenuOpen(false)
            openCreateSpaceDialog()
          }}
        >
          <Plus className="size-4" />
          <span className="text-sm">Create new site</span>
        </button>
      ) : (
        <>
          <button
            className="hover:bg-accent flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left"
            onClick={() => {
              setMobileMenuOpen(false)
              goToMySite()
            }}
          >
            <Globe className="size-4" />
            <span className="flex-1 truncate text-sm">{mySiteLabel}</span>
            {mySiteUrl ? <ExternalLink className="text-muted-foreground size-4" /> : null}
          </button>
        </>
      )}
      <div className="bg-border h-px" />
      <button
        className="text-destructive hover:bg-accent flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left font-normal"
        onClick={() => {
          setMobileMenuOpen(false)
          logoutDialog.open({})
        }}
      >
        <LogOut className="size-4" />
        <span className="text-sm">Log out</span>
      </button>
    </>
  )

  return (
    <>
      {isMobile ? (
        <div className="flex items-center gap-2">
          <NotifsButton signedIn />
          <button
            ref={accountMenuTriggerRef}
            aria-label="Account menu"
            aria-expanded={mobileMenuOpen}
            className="flex cursor-pointer rounded-full shadow-lg"
            onClick={() => setMobileMenuOpen(true)}
          >
            {avatarIcon}
          </button>
          {joinButton}
          <MobilePanelSheet isOpen={mobileMenuOpen} title="" onClose={() => setMobileMenuOpen(false)}>
            <div className="font-normal [&_svg]:stroke-[1.5]">
              {accountHeader}
              {menuItems}
            </div>
          </MobilePanelSheet>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <NotifsButton signedIn />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                ref={accountMenuTriggerRef}
                aria-label="Account menu"
                className="flex cursor-pointer rounded-full shadow-lg"
              >
                {avatarIcon}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="bottom"
              align="end"
              className={accountMenuContentClassName}
              onCloseAutoFocus={(event) => {
                if (!openMaintenanceOnMenuClose.current) return
                openMaintenanceOnMenuClose.current = false
                if (maintenance && accountMenuTriggerRef.current) {
                  event.preventDefault()
                  maintenance.openDialog(accountMenuTriggerRef.current)
                }
              }}
            >
              {accountHeader}
              <AccountMenuSection>Settings</AccountMenuSection>
              <DropdownMenuItem
                onClick={() => {
                  if (accountId) {
                    navigate({key: 'profile', id: hmId(accountId, {latest: true})})
                  }
                }}
              >
                <ContactRound className="size-4" />
                My profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate({key: 'agents'})}>
                <Bot className="size-4" />
                Agents
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  if (vaultAccountSettingsUrl) {
                    window.open(vaultAccountSettingsUrl, '_blank')
                  }
                }}
                disabled={!vaultAccountSettingsUrl}
              >
                <KeyRound className="size-4" />
                Manage accounts
              </DropdownMenuItem>
              <WebSpaceSettingsMenuItem
                siteUid={siteUid}
                onSelect={() => {
                  navigate({key: 'site-settings', id: hmId(siteUid)})
                }}
              />
              <DropdownMenuItem onSelect={() => navigate({key: 'drafts'})}>
                <SquarePen className="size-4" />
                Drafts
              </DropdownMenuItem>
              {maintenance ? (
                <DropdownMenuItem
                  aria-haspopup="dialog"
                  onSelect={() => {
                    openMaintenanceOnMenuClose.current = true
                  }}
                >
                  <ListChecks className="size-4" />
                  Document maintenance
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator className="bg-black/10 dark:bg-white/10" />
              <AccountMenuSection>My sites</AccountMenuSection>
              {canCreateSpace ? (
                <DropdownMenuItem
                  onClick={openCreateSpaceDialog}
                  className="font-normal text-green-600 focus:text-green-600 dark:text-green-500 dark:focus:text-green-500"
                >
                  <Plus className="size-4 text-green-600 dark:text-green-500" />
                  Create new site
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem onClick={goToMySite}>
                    <Globe className="size-4" />
                    <span className="flex-1 truncate">{mySiteLabel}</span>
                    {mySiteUrl ? <ExternalLink className="text-muted-foreground size-3.5" /> : null}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator className="bg-black/10 dark:bg-white/10" />
              <DropdownMenuItem className="font-normal" variant="destructive" onClick={() => logoutDialog.open({})}>
                <LogOut className="size-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {joinButton}
        </div>
      )}
      {logoutDialog.content}
      {createAccountContent}
      {createSpaceDialogContent}
    </>
  )
}

/**
 * Shared shell for site-scoped web pages that need consistent top-level chrome.
 */
export function WebSitePageShell({children, siteUid}: {children?: ReactNode; siteUid: string}) {
  const {origin, originHomeId} = useUniversalAppContext()
  const shouldShowHostBanner = origin && originHomeId && siteUid !== originHomeId.uid

  return (
    <>
      {shouldShowHostBanner ? <HypermediaHostBanner origin={origin} /> : null}
      {children}
    </>
  )
}

// Toggles the space Activity panel, or collapses the full Activity page. It opens on To you, or on All updates for a
// signed-out reader, who has no notifications but can still follow the space.
function NotifsButton({signedIn}: {signedIn: boolean}) {
  const tab = signedIn ? 'to-you' : 'all-updates'
  const route = useNavRoute()
  const navigate = useNavigate()
  const isActive = isActivityOpen(route)
  const {originHomeId} = useUniversalAppContext()
  // Gateway pages (/hm/<uid>/...) belong to the space in their route, not to the gateway's own site.
  const siteUid = getRouteSpaceUid(route) ?? originHomeId?.uid
  const collapse = useCollapseActivity(hmId(siteUid), route.key === 'feed' ? route.tab : tab)
  const inbox = useWebNotificationInbox(siteUid)
  const readState = useWebNotificationReadState(siteUid)

  const unreadCount = useMemo(() => {
    const notifications = inbox.data?.notifications ?? []
    if (!notifications.length || !readState.data) return 0
    return notifications.filter(
      (item) =>
        !isNotificationEventRead({
          readState: readState.data,
          eventId: item.feedEventId,
          eventAtMs: item.eventAtMs,
        }),
    ).length
  }, [inbox.data, readState.data])

  return (
    <Tooltip content="Activity" asChild>
      <Button
        className={cn(
          'relative h-8 rounded-full border-1 border-transparent p-0',
          isActive && 'dark:bg-muted bg-black/5',
        )}
        variant="ghost"
        size="icon"
        aria-label="Activity"
        aria-pressed={isActive}
        onClick={() => {
          const target = getActivityBellTarget(route, tab)
          if (target === 'collapse') collapse()
          else if (target) navigate(target)
          // Pages outside any space have no panel to open, so open the site's full Activity page.
          else if (siteUid) navigate({key: 'feed', id: hmId(siteUid), tab})
        }}
      >
        <Bell className="size-4" />
        {unreadCount > 0 ? (
          <>
            <span aria-hidden="true" className="absolute top-1 right-1 size-2 rounded-full bg-red-500" />
            <span className="sr-only">{unreadCount} unread notifications</span>
          </>
        ) : null}
      </Button>
    </Tooltip>
  )
}
